import { DocTable, InfoCallout } from './index';
import styles from './DocContent.module.css';

const requirementsColumns = [
  { header: '#', accessor: 'num' },
  { header: 'Domain', accessor: 'domain', mono: true },
  { header: 'Port(s)', accessor: 'ports', mono: true },
  { header: 'Protocol', accessor: 'protocol' },
  { header: 'Direction', accessor: 'direction' },
];

const requirementsData = [
  {
    num: '1',
    domain: 'api.interlucent.ai',
    ports: '443',
    protocol: 'HTTPS',
    direction: 'Outbound',
  },
  {
    num: '2',
    domain: 'api.interlucent.ai',
    ports: '443',
    protocol: 'WSS',
    direction: 'Outbound',
  },
  {
    num: '3',
    domain: 'stun.cloudflare.com',
    ports: '3478',
    protocol: 'UDP (STUN)',
    direction: 'Outbound',
  },
  {
    num: '4',
    domain: '*.turn.cloudflare.com',
    ports: '3478, 443',
    protocol: 'UDP/TCP/TLS (TURN)',
    direction: 'Outbound',
  },
  {
    num: '5',
    domain: 'Peer IP (or TURN relay)',
    ports: '49152–65535',
    protocol: 'UDP (DTLS 1.2/SRTP)',
    direction: 'Outbound',
  },
];

const minRequirementsColumns = [
  { header: '#', accessor: 'num' },
  { header: 'Domain', accessor: 'domain', mono: true },
  { header: 'Port', accessor: 'ports', mono: true },
  { header: 'Protocol', accessor: 'protocol' },
  { header: 'Direction', accessor: 'direction' },
];

const minRequirementsData = [
  {
    num: '1',
    domain: 'api.interlucent.ai',
    ports: '443',
    protocol: 'HTTPS',
    direction: 'Outbound',
  },
  {
    num: '2',
    domain: 'api.interlucent.ai',
    ports: '443',
    protocol: 'WSS',
    direction: 'Outbound',
  },
  {
    num: '3',
    domain: '*.turn.cloudflare.com',
    ports: '443',
    protocol: 'TLS (TURNS)',
    direction: 'Outbound',
  },
];

export function NetworkConnectivityRequirementsContent() {
  return (
    <>
      <h1 className={styles.pageTitle}>Network Connectivity Requirements</h1>
      <p className={styles.pageSubtitle}>
        Domains, ports, and protocols that must be allowed for the{' '}
        <code className={styles.mono}>&lt;pixel-stream&gt;</code> component to
        work through firewalls, proxies, and other network security appliances.
      </p>
      <InfoCallout variant="info">
        Provide this document to your network/security team when requesting
        firewall changes. All connections are <strong>outbound only</strong> — no
        inbound firewall rules are required. Media return traffic is permitted
        via standard stateful firewall/NAT session tracking. No unsolicited
        inbound connections are accepted.
      </InfoCallout>

      {/* ── Overview ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="overview">
          Overview
        </h2>
        <p className={styles.prose}>
          The pixel-stream component establishes three categories of outbound
          network connections:
        </p>
        <ol className={styles.stepList}>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>1</span>
            <span>
              <strong>HTTPS</strong> — token exchange (admission token → access
              token)
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>2</span>
            <span>
              <strong>WebSocket (WSS)</strong> — signaling and session management
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>3</span>
            <span>
              <strong>WebRTC (UDP/TCP)</strong> — media transport (video, audio,
              data channels)
            </span>
          </li>
        </ol>
      </section>

      {/* ── 1. Gateway API ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="gateway-api">
          Gateway API (HTTPS)
        </h2>
        <DocTable
          columns={[
            { header: 'Item', accessor: 'item' },
            { header: 'Value', accessor: 'value', mono: true },
          ]}
          data={[
            { item: 'Domain', value: 'api.interlucent.ai' },
            { item: 'Port', value: '443' },
            { item: 'Protocol', value: 'HTTPS (TLS 1.2+)' },
            { item: 'Path', value: 'POST /agent/token' },
            {
              item: 'Purpose',
              value: 'Exchanges an admission JWT for an access token and ICE server list',
            },
          ]}
        />
        <p className={styles.prose}>
          The component calls this endpoint once at startup. The response includes
          the access token used for all subsequent connections and a set of
          TURN/STUN credentials.
        </p>
      </section>

      {/* ── 2. Signaling WebSocket ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="signaling-websocket">
          Signaling WebSocket (WSS)
        </h2>
        <DocTable
          columns={[
            { header: 'Item', accessor: 'item' },
            { header: 'Value', accessor: 'value', mono: true },
          ]}
          data={[
            { item: 'Domain', value: 'api.interlucent.ai' },
            { item: 'Port', value: '443' },
            { item: 'Protocol', value: 'WSS (WebSocket over TLS)' },
            { item: 'Path', value: '/ws/session' },
            {
              item: 'Purpose',
              value: 'WebRTC signaling, heartbeat, session lifecycle',
            },
          ]}
        />
        <p className={styles.prose}>
          The WebSocket remains open for the duration of the streaming session. It
          carries WebRTC signaling messages (SDP offers/answers, ICE candidates),
          heartbeat pings every 30 seconds (timeout at 90 s), session keepalive
          pings every 25 seconds, and streamer presence updates.
        </p>
        <InfoCallout variant="warning">
          Network appliances performing deep packet inspection must allow the
          WebSocket{' '}
          <code className={styles.mono}>Upgrade</code> handshake on this path
          and must not terminate idle connections within the heartbeat window.
          HTTP/1.1 is required for the WebSocket upgrade — proxies that only
          support HTTP/2 or strip the{' '}
          <code className={styles.mono}>Upgrade</code> header will break
          signaling.
        </InfoCallout>
      </section>

      {/* ── 3. STUN ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="stun">
          STUN (UDP)
        </h2>
        <DocTable
          columns={[
            { header: 'Item', accessor: 'item' },
            { header: 'Value', accessor: 'value', mono: true },
          ]}
          data={[
            { item: 'Domain', value: 'stun.cloudflare.com' },
            { item: 'Port', value: '3478' },
            { item: 'Protocol', value: 'UDP (STUN)' },
            {
              item: 'Purpose',
              value: "NAT traversal — discovers the client's public IP and port mapping",
            },
          ]}
        />
        <p className={styles.prose}>
          STUN is a lightweight, unauthenticated protocol. If UDP 3478 is
          blocked, the component falls back to TURN relay. Direct UDP (STUN +
          ephemeral ports) is optional — TURN is required for guaranteed
          connectivity.
        </p>
      </section>

      {/* ── 4. TURN Relay ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="turn-relay">
          TURN Relay (UDP + TCP + TLS)
        </h2>
        <p className={styles.prose}>
          TURN servers are provisioned dynamically by the Cloudflare Realtime API
          during the token exchange step. The exact server addresses are not
          static, but they fall under Cloudflare's realtime infrastructure.
        </p>
        <DocTable
          columns={[
            { header: 'Item', accessor: 'item' },
            { header: 'Value', accessor: 'value', mono: true },
          ]}
          data={[
            { item: 'Domain', value: '*.turn.cloudflare.com' },
            { item: 'Ports', value: '3478 (UDP + TCP), 443 (TLS/TCP)' },
            {
              item: 'Protocol',
              value: 'TURN over UDP; TURN over TCP; TURNS over TLS',
            },
            {
              item: 'Purpose',
              value: 'Media relay when direct peer-to-peer connection fails',
            },
          ]}
        />
        <p className={styles.prose}>
          Credentials are short-lived (TTL: 8 hours), session-scoped, and
          generated server-side. Port 53 (DNS) is automatically filtered from
          TURN candidates to avoid conflicts with DNS resolution.
        </p>
        <InfoCallout variant="tip">
          If the network only allows outbound TCP 443 (strict corporate
          environments), the TURNS (TURN-over-TLS on port 443) path must be open.
          This is the last-resort relay and is critical for connectivity in
          restrictive networks. TURN over TLS on port 443 is the minimum
          enterprise requirement.
        </InfoCallout>
      </section>

      {/* ── 5. WebRTC Media ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="webrtc-media">
          WebRTC Media (UDP)
        </h2>
        <DocTable
          columns={[
            { header: 'Item', accessor: 'item' },
            { header: 'Value', accessor: 'value', mono: true },
          ]}
          data={[
            { item: 'Ports', value: '49152–65535 (ephemeral)' },
            { item: 'Protocol', value: 'DTLS 1.2 + SRTP (AES-128-GCM) over UDP' },
            {
              item: 'Purpose',
              value: 'Encrypted video, audio, and data channel transport',
            },
          ]}
        />
        <p className={styles.prose}>
          Direct peer-to-peer WebRTC connections use ephemeral UDP ports. If the
          firewall cannot allow a wide UDP range, ensure TURN relay is reachable
          so that all media can be tunneled through it.
        </p>
        <DocTable
          columns={[
            { header: 'Metric', accessor: 'metric' },
            { header: 'Typical range', accessor: 'value', mono: true },
          ]}
          data={[
            { metric: 'Bandwidth', value: '5–50 Mbps' },
            { metric: 'Packet size', value: '~1200 bytes' },
            { metric: 'Packet rate', value: '~1000–3000 packets/sec' },
          ]}
        />
        <InfoCallout variant="warning">
          <strong>Connect-then-fail scenario:</strong> Some networks allow STUN
          and the DTLS handshake but block sustained SRTP traffic (e.g. via UDP
          payload inspection or rate limiting). This causes a pattern where the
          connection appears to establish but video freezes or drops shortly
          after. If this occurs, switch to TURN relay mode by setting{' '}
          <code className={styles.mono}>force-relay=&quot;true&quot;</code>.
        </InfoCallout>
      </section>

      {/* ── Minimum Requirements ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="minimum-viable">
          Minimum Requirements
        </h2>
        <p className={styles.prose}>
          For the most restrictive corporate networks, only three rules are
          required:
        </p>
        <DocTable columns={minRequirementsColumns} data={minRequirementsData} />
        <p className={styles.prose}>
          This forces all media through the TURN relay over TLS, which works
          even in heavily restricted environments. Set the component attribute{' '}
          <code className={styles.mono}>force-relay=&quot;true&quot;</code> to
          enforce relay-only mode.
        </p>
        <InfoCallout variant="tip">
          <strong>Recommendation:</strong> Use relay-preferred mode (
          <code className={styles.mono}>force-relay=&quot;true&quot;</code>) for
          corporate networks. Direct UDP is optional and provides lower latency
          when available, but TURN is required for guaranteed connectivity.
        </InfoCallout>
      </section>

      {/* ── Summary Table ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="summary">
          Full Requirements
        </h2>
        <DocTable columns={requirementsColumns} data={requirementsData} />
      </section>

      {/* ── Console ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="console">
          Interlucent Console
        </h2>
        <p className={styles.prose}>
          The rules above cover end users viewing a stream. Users of the
          Interlucent Console (dashboard) additionally require the following
          host, which carries the console&apos;s real-time organization
          channel:
        </p>
        <DocTable
          columns={[
            { header: 'Item', accessor: 'item' },
            { header: 'Value', accessor: 'value', mono: true },
          ]}
          data={[
            { item: 'Domain', value: 'dash.interlucent.ai' },
            { item: 'Port', value: '443' },
            { item: 'Protocol', value: 'HTTPS + WSS (WebSocket over TLS)' },
            { item: 'Path', value: '/ws/organization' },
            {
              item: 'Purpose',
              value: 'Organization state sync, commands, upload coordination',
            },
          ]}
        />
        <InfoCallout variant="warning">
          This host is required in all browsers. If it is blocked, the console
          loads but stays disconnected, and the browser console shows repeated{' '}
          <code className={styles.mono}>
            WebSocket connection to &apos;wss://dash.interlucent.ai/ws/organization&apos; failed
          </code>{' '}
          errors with close code 1006. Apply the same WebSocket upgrade, idle
          timeout, and TLS inspection allowances as for{' '}
          <code className={styles.mono}>api.interlucent.ai</code>.
        </InfoCallout>
      </section>

      {/* ── Notes for Admins ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="notes">
          Notes for Network Administrators
        </h2>
        <div className={styles.conceptList}>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>Outbound only</div>
            <div className={styles.conceptDef}>
              All connections are outbound only. No inbound firewall rules, port
              forwarding, or NAT configuration is required. Media return traffic
              is permitted via standard stateful firewall/NAT session tracking.
              No unsolicited inbound connections are accepted.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>Proxy idle timeout</div>
            <div className={styles.conceptDef}>
              Set proxy/firewall idle timeouts to &ge;120 seconds (300 seconds
              recommended) to avoid dropping the signaling WebSocket between
              heartbeats.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>HTTP/1.1 required</div>
            <div className={styles.conceptDef}>
              The WebSocket upgrade handshake requires HTTP/1.1. The token
              exchange endpoint also supports HTTP/2.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>TLS inspection</div>
            <div className={styles.conceptDef}>
              If performing TLS interception, the WebSocket upgrade on{' '}
              <code className={styles.mono}>/ws/session</code> and TURNS
              connections must be allowed. Certificate pinning is not used, so
              transparent proxies will work if the proxy CA is trusted by the
              client.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>
              ICE success &ne; media success
            </div>
            <div className={styles.conceptDef}>
              ICE negotiation may succeed while media still fails if the network
              inspects or blocks sustained UDP traffic (SRTP). Test with actual
              streaming, not just connectivity checks.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>Cloudflare IP ranges</div>
            <div className={styles.conceptDef}>
              If domain-based firewall rules are not supported, use
              Cloudflare&apos;s published IP ranges:{' '}
              <a
                href="https://www.cloudflare.com/ips/"
                target="_blank"
                rel="noopener noreferrer"
              >
                cloudflare.com/ips
              </a>
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>Troubleshooting</div>
            <div className={styles.conceptDef}>
              To verify ICE connectivity independently, use the{' '}
              <a
                href="https://webrtc.github.io/samples/src/content/peerconnection/trickle-ice/"
                target="_blank"
                rel="noopener noreferrer"
              >
                WebRTC trickle ICE test page
              </a>
              .
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
